import React from 'react';
import '../../styles/button.scss';

/**
 * Button Component - Reusable button with glassmorphism styling
 *
 * Centralized button component with all design variants used across the application.
 * No need to create custom button classes - use the appropriate variant instead.
 *
 * @param {React.ReactNode} children - Button content
 * @param {'primary'|'secondary'|'danger'|'success'|'warning'|'ghost'|'action'|'action-ghost'|'toggle'|'more-replies'|'outline'|'filled'|'modal-save'|'edit'|'primary-purple'|'secondary-outline'} variant - Button style variant
 * @param {'small'|'medium'|'large'} size - Button size
 * @param {boolean} disabled - Disabled state
 * @param {boolean} loading - Loading state with spinner
 * @param {React.ReactNode} icon - Icon element
 * @param {'left'|'right'} iconPosition - Icon position
 * @param {boolean} fullWidth - Full width button
 * @param {Function} onClick - Click handler
 * @param {'button'|'submit'|'reset'} type - Button type
 * @param {string} className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 *
 * @example
 * // Primary button
 * <Button variant="primary">Save</Button>
 *
 * @example
 * // Outline button with icon (used in Experience/Education sections)
 * <Button variant="outline" icon={<Icon name="Add_Plus" />}>Add experience</Button>
 *
 * @example
 * // Filled button (empty state CTA)
 * <Button variant="filled" icon={<Icon name="Add" />}>Add your first experience</Button>
 *
 * @example
 * // Modal save button
 * <Button variant="modal-save" type="submit">Save</Button>
 *
 * @example
 * // Edit button (icon only)
 * <Button variant="edit" icon={<Icon name="Edit_Pencil_02" />} aria-label="Edit" />
 */
export const Button = React.forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      disabled = false,
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      onClick,
      type = 'button',
      className = '',
      ...props
    },
    ref
  ) => {
    const buttonClasses = [
      'btn',
      `btn--${variant}`,
      `btn--${size}`,
      disabled && 'btn--disabled',
      loading && 'btn--loading',
      fullWidth && 'btn--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClick = (e) => {
      if (disabled || loading) return;
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        onClick={handleClick}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <span className='btn__spinner'>⟳</span>}

        {!loading && icon && iconPosition === 'left' && (
          <span className='btn__icon btn__icon--left'>{icon}</span>
        )}

        {children && <span className='btn__text'>{children}</span>}

        {!loading && icon && iconPosition === 'right' && (
          <span className='btn__icon btn__icon--right'>{icon}</span>
        )}
      </button>
    );
  }
);
